export interface ParsedCardExpiry {
  month: number;
  year: number;
}

export interface CardExpiryValidation {
  isValid: boolean;
  errorMessage?: "Expired" | "Invalid Expiry Date";
}

export type CardExpiryStatus =
  | "valid"
  | "expiring-soon"
  | "expired"
  | "invalid";

/**
 * Parse gateway expiry values without relying on browser date parsing.
 * Gateways used by the portal return both M(M)/YY and M(M)/YYYY.
 */
export const parseCardExpiry = (value: unknown): ParsedCardExpiry | null => {
  if (typeof value !== "string") return null;

  const match = /^(0?[1-9]|1[0-2])\/(\d{2}|\d{4})$/.exec(value.trim());
  if (!match) return null;

  const month = Number(match[1]);
  const suppliedYear = Number(match[2]);
  const year = match[2].length === 2 ? 2000 + suppliedYear : suppliedYear;

  if (!Number.isInteger(year) || year < 1) return null;

  return { month, year };
};

export const getCardExpiryStatus = (
  value: unknown,
  now: Date = new Date()
): CardExpiryStatus => {
  const expiry = parseCardExpiry(value);
  if (!expiry) return "invalid";

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (
    expiry.year < currentYear ||
    (expiry.year === currentYear && expiry.month < currentMonth)
  ) {
    return "expired";
  }

  if (expiry.year === currentYear && expiry.month === currentMonth) {
    return "expiring-soon";
  }

  return "valid";
};

export const isCreditCardExpired = (
  value: unknown,
  now: Date = new Date()
) => getCardExpiryStatus(value, now) === "expired";

export const isCardExpiredSoon = (
  value: unknown,
  now: Date = new Date()
) => getCardExpiryStatus(value, now) === "expiring-soon";

export const getCardExpiryFlags = (
  value: unknown,
  now: Date = new Date()
) => {
  const status = getCardExpiryStatus(value, now);
  return {
    isExpired: status === "expired",
    isExpireSoon: status === "expiring-soon",
  };
};

export const isCardExpiryUsable = (
  value: unknown,
  now: Date = new Date()
) => {
  const status = getCardExpiryStatus(value, now);
  return status === "valid" || status === "expiring-soon";
};

export const validateCardExpiry = (
  value: unknown,
  now: Date = new Date()
): CardExpiryValidation => {
  const status = getCardExpiryStatus(value, now);

  if (status === "invalid") {
    return { isValid: false, errorMessage: "Invalid Expiry Date" };
  }

  if (status === "expired") {
    return { isValid: false, errorMessage: "Expired" };
  }

  return { isValid: true };
};
